import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDebouncedValue } from '@/hooks/use-debounce';
import {
  useAssignCandidate,
  useCandidateDuplicateCheck,
  useCheckCandidateDuplicates,
  useCommitFromCv,
  useCreateCandidate,
  useParseCvStandalone,
  useParseJobStatus,
} from '@/hooks/use-candidates';
import { useVacancies, useVacancy } from '@/hooks/use-recruitment';
import { addCandidateSkillFromVacancy } from '@/lib/candidates';
import type {
  CandidateSource,
  CreateCandidateInput,
  DuplicateMatch,
  ParseConflict,
  ParseJob,
  StandaloneParseResult,
} from '@/types/candidates';
import { CANDIDATE_SOURCE_OPTIONS } from '../lib/candidate-source';
import { ContactConflictModal } from './contact-conflict-modal';
import { EmployeeMultiSelect, type EmployeeOption } from './employee-multi-select';
import { useAuthStore } from '@/stores/auth';
import { extractErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { isAxiosError } from 'axios';
import {
  AlertTriangle,
  Briefcase,
  ExternalLink,
  Plus,
  Search,
  Sparkles,
  UploadCloud,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  RECRUITMENT_PARSER_EXTENSION_REGEX,
  RECRUITMENT_PARSER_EXTENSIONS,
  RECRUITMENT_PARSER_HINT,
} from '@hygge/shared';

interface CreateCandidateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (candidateId: string) => void;
  /** Pre-link new candidate to this vacancy. Picker is hidden when set. */
  initialVacancyId?: string;
  /** Display title for the pre-linked vacancy (shown in the locked chip). */
  initialVacancyTitle?: string;
}

const PARSEABLE_EXT = RECRUITMENT_PARSER_EXTENSION_REGEX;
const PARSER_ACCEPT = RECRUITMENT_PARSER_EXTENSIONS.join(',');

export function CreateCandidateDialog({
  open,
  onOpenChange,
  onSuccess,
  initialVacancyId,
  initialVacancyTitle,
}: CreateCandidateDialogProps) {
  const vacancyLocked = !!initialVacancyId;
  const currentUser = useAuthStore((s) => s.user);
  // HW-541: default Responsible Recruiter is the user creating the profile,
  // overridable via the picker before submit.
  const defaultRecruiters = useMemo<EmployeeOption[]>(
    () =>
      currentUser?.employee
        ? [
            {
              id: currentUser.employee.id,
              firstName: currentUser.employee.firstName,
              lastName: currentUser.employee.lastName,
              avatarUrl: currentUser.employee.avatarUrl ?? null,
            },
          ]
        : [],
    [currentUser],
  );

  const [fullName, setFullName] = useState('');
  // HW-705: Source is a mandatory field on create; '' means "not yet picked".
  const [source, setSource] = useState<CandidateSource | ''>('');
  const [headline, setHeadline] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [responsibleRecruiters, setResponsibleRecruiters] =
    useState<EmployeeOption[]>(defaultRecruiters);
  const [parsedSkills, setParsedSkills] = useState<{ id: string; name: string }[]>([]);

  const [emailDuplicate, setEmailDuplicate] = useState<DuplicateMatch | null>(null);
  const [softDuplicate, setSoftDuplicate] = useState<DuplicateMatch | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [parseJobId, setParseJobId] = useState<string | null>(null);
  // HW-529: when CV parse yields email/phone that differs from what the recruiter
  // already typed, queue conflicts so the user resolves them one at a time
  // (modal handles a single conflict; we shift the head on close).
  const [parseConflicts, setParseConflicts] = useState<ParseConflict[]>([]);
  const cvFileInputRef = useRef<HTMLInputElement | null>(null);

  const [vacancyPickerOpen, setVacancyPickerOpen] = useState(false);
  const [vacancySearch, setVacancySearch] = useState('');
  const [selectedVacancy, setSelectedVacancy] = useState<{ id: string; title: string } | null>(
    null,
  );
  const [pendingVacancySkillIds, setPendingVacancySkillIds] = useState<Set<string>>(
    new Set(),
  );

  const createMutation = useCreateCandidate();
  const dupMutation = useCheckCandidateDuplicates();
  const parseMutation = useParseCvStandalone();
  const commitMutation = useCommitFromCv();
  const parseStatus = useParseJobStatus(parseJobId);
  const assignMutation = useAssignCandidate();

  const debouncedVacancySearch = useDebouncedValue(vacancySearch, 300);
  const vacanciesQuery = useVacancies({
    status: 'ACTIVE',
    limit: 10,
    search: debouncedVacancySearch || undefined,
  });
  const vacancyDetailQuery = useVacancy(selectedVacancy?.id ?? '');

  const hasParseResult = parseStatus.data?.status === 'SUCCEEDED';
  const isParsing =
    parseMutation.isPending ||
    parseStatus.data?.status === 'QUEUED' ||
    parseStatus.data?.status === 'RUNNING';

  useEffect(() => {
    if (!open) {
      setFullName('');
      setSource('');
      setHeadline('');
      setEmail('');
      setPhone('');
      setParsedSkills([]);
      setEmailDuplicate(null);
      setSoftDuplicate(null);
      setErrors({});
      setSubmitError(null);
      setParseJobId(null);
      setParseConflicts([]);
      setVacancyPickerOpen(false);
      setVacancySearch('');
      setSelectedVacancy(null);
      setPendingVacancySkillIds(new Set());
    } else if (vacancyLocked) {
      setSelectedVacancy({
        id: initialVacancyId!,
        title: initialVacancyTitle ?? '',
      });
    }
  }, [open, vacancyLocked, initialVacancyId, initialVacancyTitle]);

  // Prefill the Responsible Recruiter with the current user each time the modal
  // opens. Deps intentionally exclude defaultRecruiters so a late auth refresh
  // doesn't wipe the recruiter's edits while the modal is open.
  useEffect(() => {
    if (open) setResponsibleRecruiters(defaultRecruiters);
  }, [open]);

  useEffect(() => {
    const job = parseStatus.data;
    if (!job || !parseJobId) return;
    if (job.status === 'SUCCEEDED') {
      applyParsedResult(job);
    } else if (job.status === 'FAILED') {
      setSubmitError(job.error ?? 'CV parsing failed — fill in the form manually.');
      setParseJobId(null);
    }
  }, [parseStatus.data, parseJobId]);

  const applyParsedResult = (job: ParseJob) => {
    const result = job.result as StandaloneParseResult | null;
    if (!result) return;
    const p = result.parsedFields;
    setFullName((cur) => cur || p.fullName || '');

    // HW-529 / Story 4 AC4: when the recruiter already typed an email or phone
    // and the CV carries a different value, defer to a conflict modal instead
    // of silently dropping the parsed value. Empty fields still auto-fill.
    const conflicts: ParseConflict[] = [];
    const parsedEmail = p.email?.trim() ?? '';
    const manualEmail = email.trim();
    if (parsedEmail) {
      if (manualEmail && parsedEmail.toLowerCase() !== manualEmail.toLowerCase()) {
        conflicts.push({ field: 'email', existingValue: manualEmail, parsedValue: parsedEmail });
      } else if (!manualEmail) {
        setEmail(parsedEmail);
      }
    }
    const parsedPhone = p.phone?.trim() ?? '';
    const manualPhone = phone.trim();
    if (parsedPhone) {
      if (manualPhone && parsedPhone !== manualPhone) {
        conflicts.push({ field: 'phone', existingValue: manualPhone, parsedValue: parsedPhone });
      } else if (!manualPhone) {
        setPhone(parsedPhone);
      }
    }
    if (conflicts.length > 0) setParseConflicts(conflicts);

    setParsedSkills(
      p.skillIds.map((id, i) => ({ id, name: p.skillNames[i] ?? id })),
    );
  };

  const headConflict = parseConflicts[0] ?? null;
  const handleConflictApply = (value: string) => {
    if (!headConflict) return;
    if (headConflict.field === 'email') {
      setEmail(value);
      setEmailDuplicate(null);
    } else {
      setPhone(value);
    }
  };
  const dismissHeadConflict = () => setParseConflicts((prev) => prev.slice(1));

  const debouncedName = useDebouncedValue(fullName, 400);
  const debouncedPhone = useDebouncedValue(phone, 400);
  const trimmedDebouncedName = debouncedName.trim();
  const trimmedDebouncedPhone = debouncedPhone.trim();
  const softDuplicateEnabled =
    trimmedDebouncedName.length > 0 && trimmedDebouncedPhone.length > 0;
  const softDupQuery = useCandidateDuplicateCheck(
    softDuplicateEnabled
      ? { fullName: trimmedDebouncedName, phone: trimmedDebouncedPhone }
      : null,
    softDuplicateEnabled,
  );

  useEffect(() => {
    if (!softDuplicateEnabled) {
      setSoftDuplicate(null);
      return;
    }
    if (softDupQuery.data) {
      const soft =
        softDupQuery.data.matches.find((m) => m.matchScore === 'SOFT') ?? null;
      setSoftDuplicate(soft);
    }
  }, [softDuplicateEnabled, softDupQuery.data]);

  const handleEmailBlur = async () => {
    const value = email.trim();
    if (!value) {
      setEmailDuplicate(null);
      return;
    }
    try {
      const res = await dupMutation.mutateAsync({ email: value });
      const hard = res.matches.find(
        (m) => m.matchScore === 'HARD' && m.matchedFields.includes('email')
      );
      setEmailDuplicate(hard ?? null);
    } catch {
      setEmailDuplicate(null);
    }
  };

  const handleCvPick = async (file: File | null | undefined) => {
    if (!file) return;
    setSubmitError(null);
    try {
      const { parseJobId: jobId } = await parseMutation.mutateAsync(file);
      setParseJobId(jobId);
    } catch (err) {
      setSubmitError(extractErrorMessage(err, 'Failed to upload CV'));
    }
  };

  const isSubmitting =
    createMutation.isPending || commitMutation.isPending || assignMutation.isPending;

  const canSubmit = useMemo(() => {
    if (!fullName.trim()) return false;
    if (!source) return false;
    if (emailDuplicate) return false;
    if (isSubmitting) return false;
    if (isParsing) return false;
    return true;
  }, [fullName, source, emailDuplicate, isSubmitting, isParsing]);

  const togglePendingVacancySkill = (skillId: string) => {
    setPendingVacancySkillIds((prev) => {
      const next = new Set(prev);
      if (next.has(skillId)) next.delete(skillId);
      else next.add(skillId);
      return next;
    });
  };

  const clearSelectedVacancy = () => {
    setSelectedVacancy(null);
    setPendingVacancySkillIds(new Set());
  };

  const attachPendingVacancySkills = async (
    candidateId: string,
    vacancyId: string,
    skillIds: Iterable<string>,
  ): Promise<void> => {
    const ids = Array.from(skillIds);
    if (ids.length === 0) return;
    await Promise.all(
      ids.map((skillId) =>
        addCandidateSkillFromVacancy(candidateId, skillId, vacancyId).catch((err) => {
          // Swallow "already added" — e.g. skill was also parsed from CV.
          if (isAxiosError(err) && err.response?.status === 409) return;
          throw err;
        }),
      ),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const newErrors: Record<string, string> = {};
    if (!fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!source) newErrors.source = 'Source is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    // The validation gate above guarantees a Source was picked; the redundant
    // check narrows `source` to CandidateSource so both create paths can send
    // it without a cast (HW-705).
    if (!source) return;

    try {
      let candidateId: string;

      const responsibleRecruiterIds = responsibleRecruiters.map((r) => r.id);

      if (parseJobId && hasParseResult) {
        const created = await commitMutation.mutateAsync({
          parseJobId,
          overrides: {
            fullName: fullName.trim(),
            source,
            headline: headline.trim() || undefined,
            email: email.trim() || undefined,
            phone: phone.trim() || undefined,
            skillIds: parsedSkills.map((s) => s.id),
          },
          vacancyId: selectedVacancy?.id,
          responsibleRecruiterIds,
        });
        candidateId = created.id;
      } else {
        const payload: CreateCandidateInput = {
          fullName: fullName.trim(),
          source,
          responsibleRecruiterIds,
        };
        if (headline.trim()) payload.headline = headline.trim();
        if (email.trim()) payload.email = email.trim();
        if (phone.trim()) payload.phone = phone.trim();

        const created = await createMutation.mutateAsync(payload);
        candidateId = created.id;

        if (selectedVacancy) {
          try {
            await assignMutation.mutateAsync({
              id: candidateId,
              input: { vacancyId: selectedVacancy.id },
            });
          } catch (err) {
            if (!(isAxiosError(err) && err.response?.status === 409)) throw err;
          }
        }
      }

      if (selectedVacancy && pendingVacancySkillIds.size > 0) {
        await attachPendingVacancySkills(
          candidateId,
          selectedVacancy.id,
          pendingVacancySkillIds,
        );
      }

      onOpenChange(false);
      onSuccess(candidateId);
    } catch (err: unknown) {
      if (isAxiosError(err) && err.response?.status === 409) {
        const body = err.response.data as {
          code?: string;
          message?: string;
          existingCandidateId?: string;
        };
        if (body.code === 'CANDIDATE_EMAIL_DUPLICATE' && body.existingCandidateId) {
          setEmailDuplicate({
            candidateId: body.existingCandidateId,
            fullName: '',
            matchedFields: ['email'],
            matchScore: 'HARD',
            status: 'ACTIVE',
          });
        } else {
          setSubmitError(body.message ?? 'Duplicate candidate');
        }
      } else {
        setSubmitError(extractErrorMessage(err, 'Failed to create candidate. Try again.'));
      }
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle>New Candidate</DialogTitle>
          <DialogDescription>
            Drop a CV to auto-fill, or fill the basics manually.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* CV drop-zone */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => cvFileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                cvFileInputRef.current?.click();
              }
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              const dropped = e.dataTransfer.files?.[0];
              if (dropped && PARSEABLE_EXT.test(dropped.name)) {
                void handleCvPick(dropped);
              }
            }}
            className={cn(
              'flex cursor-pointer items-center gap-3 rounded-md border border-dashed px-3 py-3 text-sm transition-colors',
              isDragging
                ? 'border-hygge bg-hygge/10'
                : hasParseResult
                  ? 'border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/20'
                  : 'border-border bg-muted/20 hover:bg-muted/40',
            )}
          >
            {isParsing ? (
              <>
                <Sparkles className="h-4 w-4 animate-pulse text-amber-500" />
                <div className="flex-1 text-muted-foreground">
                  Parsing CV… fields will auto-fill in a moment.
                </div>
              </>
            ) : hasParseResult ? (
              <>
                <Sparkles className="h-4 w-4 text-emerald-600" />
                <div className="flex-1 text-foreground">
                  CV parsed · fields pre-filled below.
                  {parsedSkills.length > 0 && (
                    <span className="text-muted-foreground"> {parsedSkills.length} skills extracted.</span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">Drop again to re-parse</span>
              </>
            ) : (
              <>
                <UploadCloud className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1">
                  <div className="font-medium text-foreground">Drop CV to auto-fill (optional)</div>
                  <div className="text-xs text-muted-foreground">{RECRUITMENT_PARSER_HINT}</div>
                </div>
              </>
            )}
            <input
              ref={cvFileInputRef}
              type="file"
              className="hidden"
              accept={PARSER_ACCEPT}
              onChange={(e) => {
                const picked = e.target.files?.[0] ?? null;
                if (cvFileInputRef.current) cvFileInputRef.current.value = '';
                if (picked) void handleCvPick(picked);
              }}
            />
          </div>

          {emailDuplicate && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="flex-1 flex items-center justify-between gap-3">
                <p>Candidate with this email already exists.</p>
                <a
                  href={`/recruitment/candidates/${emailDuplicate.candidateId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline inline-flex items-center gap-1 shrink-0"
                >
                  Open existing <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          )}

          {softDuplicate && !emailDuplicate && (
            <div className="rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/20 p-3 text-sm text-amber-900 dark:text-amber-200 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1 flex items-center justify-between gap-3">
                <p className="wrap-anywhere">
                  Potential duplicate: <strong>{softDuplicate.fullName}</strong>
                </p>
                <a
                  href={`/recruitment/candidates/${softDuplicate.candidateId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline inline-flex items-center gap-1 shrink-0"
                >
                  View profile <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="cand-fullName">Full Name *</Label>
            <Input
              id="cand-fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="e.g. Jane Doe"
              maxLength={255}
              autoFocus
              aria-invalid={!!errors.fullName}
            />
            {errors.fullName && <p className="text-sm text-destructive mt-1">{errors.fullName}</p>}
          </div>

          <div>
            <Label htmlFor="cand-source">Source *</Label>
            <Select value={source} onValueChange={(v) => setSource(v as CandidateSource)}>
              <SelectTrigger id="cand-source" aria-invalid={!!errors.source}>
                <SelectValue placeholder="Select source…" />
              </SelectTrigger>
              <SelectContent>
                {CANDIDATE_SOURCE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.source && <p className="text-sm text-destructive mt-1">{errors.source}</p>}
          </div>

          <div>
            <Label htmlFor="cand-headline">Headline</Label>
            <Input
              id="cand-headline"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder="e.g. Senior Frontend Engineer"
              maxLength={500}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cand-email">Email</Label>
              <Input
                id="cand-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailDuplicate) setEmailDuplicate(null);
                }}
                onBlur={handleEmailBlur}
                placeholder="jane@example.com"
                maxLength={255}
              />
            </div>
            <div>
              <Label htmlFor="cand-phone">Phone</Label>
              <Input
                id="cand-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+380..."
                maxLength={50}
              />
            </div>
          </div>

          <div>
            <Label>Responsible Recruiter</Label>
            <div className="mt-1">
              <EmployeeMultiSelect
                value={responsibleRecruiters}
                onChange={setResponsibleRecruiters}
                placeholder="Add recruiter…"
              />
            </div>
          </div>

          {/* Vacancy picker + Skills from Vacancy (HW-379) */}
          <div className="space-y-2">
            {!selectedVacancy && !vacancyPickerOpen && !vacancyLocked ? (
              <button
                type="button"
                onClick={() => setVacancyPickerOpen(true)}
                className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                <Briefcase className="h-3.5 w-3.5" />
                Link to vacancy (optional)
              </button>
            ) : null}

            {vacancyPickerOpen && !selectedVacancy && !vacancyLocked ? (
              <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={vacancySearch}
                      onChange={(e) => setVacancySearch(e.target.value)}
                      placeholder="Search active vacancies…"
                      className="h-8 pl-8 text-sm"
                      autoFocus
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setVacancyPickerOpen(false);
                      setVacancySearch('');
                    }}
                    className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label="Close vacancy picker"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="max-h-40 overflow-y-auto rounded-md border border-border bg-background">
                  {vacanciesQuery.isLoading ? (
                    <div className="p-2 text-center text-xs text-muted-foreground">Loading…</div>
                  ) : (vacanciesQuery.data?.data ?? []).length === 0 ? (
                    <div className="p-2 text-center text-xs text-muted-foreground">
                      No active vacancies.
                    </div>
                  ) : (
                    <ul>
                      {(vacanciesQuery.data?.data ?? []).map((v) => (
                        <li key={v.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedVacancy({ id: v.id, title: v.title });
                              setVacancyPickerOpen(false);
                              setVacancySearch('');
                            }}
                            className="w-full cursor-pointer border-b border-border px-3 py-1.5 text-left text-sm transition-colors last:border-b-0 hover:bg-muted/50"
                          >
                            <div className="truncate font-medium" title={v.title}>
                              {v.title}
                            </div>
                            <div
                              className="truncate text-xs text-muted-foreground"
                              title={`${v.department?.name ?? 'No department'}${v.location ? ` · ${v.location}` : ''}`}
                            >
                              {v.department?.name ?? 'No department'}
                              {v.location ? ` · ${v.location}` : ''}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ) : null}

            {selectedVacancy ? (
              <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="inline-flex min-w-0 items-center gap-1.5 text-sm">
                    <Briefcase className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="truncate font-medium" title={selectedVacancy.title}>
                      {selectedVacancy.title}
                    </span>
                  </div>
                  {!vacancyLocked && (
                    <button
                      type="button"
                      onClick={clearSelectedVacancy}
                      className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      aria-label="Unlink vacancy"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {vacancyDetailQuery.isLoading ? (
                  <p className="text-xs text-muted-foreground">Loading skills…</p>
                ) : (vacancyDetailQuery.data?.skills ?? []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Vacancy has no required skills.
                  </p>
                ) : (
                  <div>
                    <Label
                      className="block truncate text-xs"
                      title={`Skills from ${selectedVacancy.title}`}
                    >
                      Skills from {selectedVacancy.title}
                    </Label>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {(vacancyDetailQuery.data?.skills ?? []).map((skill) => {
                        const picked = pendingVacancySkillIds.has(skill.id);
                        return (
                          <button
                            key={skill.id}
                            type="button"
                            onClick={() => togglePendingVacancySkill(skill.id)}
                            className={cn(
                              'inline-flex max-w-[200px] cursor-pointer items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors',
                              picked
                                ? 'border-hygge/40 bg-hygge/15 text-hygge'
                                : 'border-dashed border-border bg-transparent text-muted-foreground hover:border-hygge/40 hover:text-hygge',
                            )}
                            title={skill.name}
                          >
                            {picked ? (
                              <X className="h-3 w-3 shrink-0" />
                            ) : (
                              <Plus className="h-3 w-3 shrink-0" />
                            )}
                            <span className="truncate">{skill.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {hasParseResult && parsedSkills.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs">Skills from CV (unverified)</Label>
              <div className="flex flex-wrap gap-1">
                {parsedSkills.map((s) => (
                  <Badge
                    key={s.id}
                    variant="outline"
                    className="max-w-[200px] cursor-pointer text-xs font-normal"
                    onClick={() =>
                      setParsedSkills((prev) => prev.filter((p) => p.id !== s.id))
                    }
                    title={`${s.name} — click to remove`}
                  >
                    <span className="truncate">{s.name}</span>
                    <span className="ml-1">×</span>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {submitError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {submitError}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="accent" disabled={!canSubmit}>
              {isSubmitting
                ? 'Creating...'
                : parseJobId && hasParseResult
                  ? 'Create from CV'
                  : 'Create candidate'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <ContactConflictModal
      conflict={headConflict}
      onApply={handleConflictApply}
      onClose={dismissHeadConflict}
    />
    </>
  );
}
